import styled from '@emotion/styled';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { ClickAndReserveInputs, Fields } from '~/features/checkout/models/fields';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';
import { SalesforceProduct } from '~/lib/salesforce/types';
import {
    ClickAndReservePayload,
    useSubmitClickAndReserve,
} from '~/services/clickAndReserve/useSubmitClickAndReserve';
import { Form, Input, Typography } from '~/shared/components';
import Button from '~/shared/components/Button';
import { ascertainPhoneNumber } from '~/shared/components/form/components/controllable-elements/phone-element/utils';
import { Grid } from '~/shared/components/Grid';
import Spinner from '~/shared/components/Spinner/Spinner';
import { useEvents } from '~/shared/hooks/useEvents';
import { transformSalesforceProductToDataLayer } from '~/shared/hooks/useEvents/helpers';
import { StorePickupOption } from '~/shared/hooks/useEvents/useGTMEvents/models';
import { useLocalStorage } from '~/shared/hooks/useLocalStorage';
import { useFrame } from '~/shared/utils';
import { getRuntimeConfig } from '~/shared/utils/config';
import { useTranslation } from '~/shared/utils/translation';
import { prettifyUrl } from '~/templates/pages/utils/prettifyUrl';
import { breakpoints } from '~/theme/breakpoints';
import Spacer from '~/theme/design-system/components/Spacer';
import { isPhoneWithoutCountryCode } from '~/utils/form-validators';
import { ReservationInfoMessage } from '~/features/product-details/components/ReservationDrawer/ReservationInfoMessage';
import { useReservationFlowStore } from '~/features/product-details/components/ReservationDrawer/useReservationFlowStore';
import styles from './ClickAndReserveForm.module.scss';

export const useValidationSchema = () => {
    const { translate } = useTranslation();

    return yup.object().shape({
        [Fields.FirstName]: yup.string().required(''),
        [Fields.LastName]: yup.string().required(''),
        [Fields.Email]: yup
            .string()
            .email(translate('message.emailValidationError'))
            .required('')
            .max(50, translate('message.inputValidationError.toLong', { length: '50' })),
        [Fields.Phone]: yup
            .string()
            .required('')
            .max(8, translate('message.phoneValidationError'))
            .matches(isPhoneWithoutCountryCode, translate('message.phoneValidationError')),
    });
};

export const ClickAndReserveForm = ({ product }: { product: SalesforceProduct }) => {
    const queryClient = useQueryClient();
    const { config } = getRuntimeConfig();
    const { translate } = useTranslation();
    const { data: frame } = useFrame();
    const { productReservationEvent, usedStorePickupOptionEvent } = useEvents(frame);
    const validationSchema = useValidationSchema();
    const store = useReservationFlowStore((state) => state.selectedStore);
    const quantity = useReservationFlowStore((state) => state.quantity);
    const completeReservation = useReservationFlowStore((state) => state.completeReservation);
    const [, setPrevCRStoreId] = useLocalStorage<string>('previouslySelectedCRStoreId');
    const customer = useCustomer();

    const methods = useForm<ClickAndReserveInputs>({
        mode: 'onBlur',
        reValidateMode: 'onChange',
        resolver: yupResolver(validationSchema),
    });

    useEffect(() => {
        if (!customer.isRegistered) return;

        methods.reset({
            [Fields.FirstName]: customer.firstName ?? '',
            [Fields.LastName]: customer.lastName ?? '',
            [Fields.Email]: customer.email ?? '',
            [Fields.Phone]: ascertainPhoneNumber(customer.phoneMobile),
        });
    }, [
        customer.email,
        customer.firstName,
        customer.isRegistered,
        customer.lastName,
        customer.phoneMobile,
    ]);

    const { mutate, isPending, isError } = useSubmitClickAndReserve({
        onSettled: (_data, error) => {
            queryClient.invalidateQueries({ queryKey: ['variant', product.id] });
            queryClient.invalidateQueries({ queryKey: ['store', store?.id] });

            if (!error && _data) {
                if (product) {
                    productReservationEvent(
                        transformSalesforceProductToDataLayer(product),
                        store?.id,
                        _data.reservationNumber,
                    );
                }
                if (store?.id) {
                    usedStorePickupOptionEvent({
                        context: StorePickupOption.ClickAndReserve,
                        shopId: store.id,
                    });
                }
                // Remember this store as the previously-selected C&R store so it can be
                // prepended the next time the reservation drawer opens.
                // IMC-2711
                if (store?.id) {
                    setPrevCRStoreId(store.id);
                }
                completeReservation(_data.reservationNumber);
            }
        },
    });
    const isValid = methods.formState.isValid;
    const storesRefUrl = frame?.settings?.pageReferences.storesPage?.url;

    const handleSubmit = ({ firstName, lastName, phoneMobile, email }: ClickAndReserveInputs) => {
        if (store) {
            const storeUrl = `${config.NEXT_PUBLIC_URL}${storesRefUrl}/${encodeURIComponent(
                prettifyUrl(store.name),
            )}?storeid=${store.id}`;

            const body: ClickAndReservePayload = {
                firstName,
                lastName,
                fullName: `${firstName} ${lastName}`,
                phoneNumber: String(phoneMobile),
                quantity,
                storeId: store.id,
                storeUrl: storesRefUrl ? storeUrl : store.name,
                productId: product.id,
                email,
            };

            mutate(body);
        }
    };

    if (isError) {
        return (
            <ReservationInfoMessage>{translate('pdp.clickAndReserveError')}</ReservationInfoMessage>
        );
    }

    return (
        <>
            <Spacer space="24px" />
            <Form methods={methods} onSubmit={handleSubmit}>
                <Grid rowGap="extrasmall" flow="row">
                    <StyledInputText
                        className=""
                        name={Fields.FirstName}
                        autoComplete="given-name"
                        label={translate('forms.fields.form.firstName')}
                        required
                    />
                    <StyledInputText
                        name={Fields.LastName}
                        autoComplete="family-name"
                        label={translate('forms.fields.form.lastName')}
                        required
                    />
                    <StyledInputText
                        name={Fields.Email}
                        autoComplete="email"
                        label={translate('forms.fields.form.mail')}
                        required
                    />
                    <StyledInputText
                        name={Fields.Phone}
                        autoComplete="tel"
                        label={translate('forms.fields.form.mobile')}
                        type="tel"
                        required
                        maxLength={8}
                    />
                    <Typography
                        as="p"
                        variant="paragraph-S"
                        className={styles.confirmReservationInfoMessage}
                    >
                        {translate('pdp.confirmReservationInfoMessage')}
                    </Typography>
                    <Typography
                        as="p"
                        variant="paragraph-S"
                        className={styles.confirmReservationInfoMessageSecondary}
                    >
                        {translate('pdp.confirmReservationInfoMessageSecondary')}
                    </Typography>
                    <Button disabled={!isValid || isPending}>
                        {translate('pdp.confirmReservation')}
                        {isPending && <Spinner />}
                    </Button>
                </Grid>
            </Form>
            <Spacer space="24px" />
        </>
    );
};

const StyledInputText = styled(Input)(() => ({
    [breakpoints.xs]: {
        fontSize: 16,
    },
}));
